import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Pencil, Trash2, Save, X, ToggleLeft, ToggleRight, ChevronRight, Settings, BookOpen, GraduationCap, ListChecks, Target, Tag, Award, Zap, CreditCard, Brain, HelpCircle, Flame } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const FIELD_LABELS = {
  key: 'Unique Key', label: 'Display Name', order: 'Sort Order',
  class_key: 'Class', chapter_key: 'Chapter', icon: 'Icon', description: 'Description',
  color: 'Colour', xp_multiplier: 'XP Multiplier', category: 'Category',
  coins_reward: 'Coins Reward', xp: 'XP Points', coins: 'Coins',
  price_monthly: 'Monthly Price', price_yearly: 'Yearly Price',
  sessions_per_week: 'Sessions/Week', zeno_queries: 'Zeno Queries/Day',
  bloom_level: "Bloom's Level", prerequisites: 'Prerequisites (comma-separated)',
  days_required: 'Days Required', badge_icon: 'Badge Icon', badge_description: 'Badge Description',
};

const FIELD_PLACEHOLDERS = {
  key: 'e.g., ratio_proportion (unique, lowercase, underscores)',
  label: 'e.g., Ratio & Proportion',
  order: 'e.g., 1, 2, 3 (display order)',
  class_key: 'Select a class',
  chapter_key: 'Select a chapter',
  icon: 'e.g., calculator, book, star',
  description: 'Brief description of this item',
  color: 'e.g., #FF6B35 or red',
  xp_multiplier: 'e.g., 1.5',
  category: 'e.g., algebra, geometry, arithmetic',
  coins_reward: 'e.g., 100 (coins given on achievement)',
  xp: 'e.g., 100',
  coins: 'e.g., 25',
  price_monthly: 'e.g., 299',
  price_yearly: 'e.g., 2499',
  sessions_per_week: 'e.g., 5',
  zeno_queries: 'e.g., 20',
  bloom_level: 'Select level (L1-L6)',
  prerequisites: 'e.g., basic_arithmetic, fractions',
  days_required: 'e.g., 7, 30, 100 (streak days needed)',
  badge_icon: 'e.g., fire, crown, trophy, shield, diamond, zap',
  badge_description: 'e.g., 7-day practice streak! Consistency is key!',
};

const BLOOM_LEVELS = ['L1-Remember', 'L2-Understand', 'L3-Apply', 'L4-Analyze', 'L5-Evaluate', 'L6-Create'];

const CONFIG_SECTIONS = [
  { key: 'config_classes', label: 'Classes', icon: GraduationCap, fields: ['key', 'label', 'order'], parent: null },
  { key: 'config_boards', label: 'Education Boards', icon: BookOpen, fields: ['key', 'label', 'order'], parent: null },
  { key: 'config_chapters', label: 'Chapters', icon: BookOpen, fields: ['key', 'label', 'class_key', 'order'], parent: 'config_classes', parentField: 'class_key', parentLabel: 'Class' },
  { key: 'config_microconcepts', label: 'Micro Concepts', icon: Brain, fields: ['key', 'label', 'chapter_key', 'class_key', 'bloom_level', 'prerequisites', 'order'], parent: 'config_chapters', parentField: 'chapter_key', parentLabel: 'Chapter' },
  { key: 'config_question_types', label: 'Question Types', icon: ListChecks, fields: ['key', 'label', 'icon', 'description', 'order'], parent: null },
  { key: 'config_difficulty_levels', label: 'Difficulty Levels', icon: Target, fields: ['key', 'label', 'color', 'xp_multiplier', 'order'], parent: null },
  { key: 'config_tags', label: 'Content Tags', icon: Tag, fields: ['key', 'label', 'category'], parent: null },
  { key: 'config_badges', label: 'Achievement Badges', icon: Award, fields: ['key', 'label', 'description', 'icon', 'coins_reward'], parent: null },
  { key: 'config_xp_rules', label: 'XP & Coin Rules', icon: Zap, fields: ['key', 'label', 'xp', 'coins', 'description'], parent: null },
  { key: 'config_subscription_plans', label: 'Subscription Plans', icon: CreditCard, fields: ['key', 'label', 'price_monthly', 'price_yearly', 'sessions_per_week', 'zeno_queries'], parent: null },
  { key: 'config_streak_rewards', label: 'Streak Rewards', icon: Flame, fields: ['key', 'label', 'days_required', 'coins_reward', 'badge_icon', 'badge_description', 'order'], parent: null },
];

function FieldInput({ field, value, onChange, parentData, section }) {
  if (field === 'class_key' && parentData.classes?.length > 0) {
    return (
      <select value={value || ''} onChange={e => onChange(e.target.value)}
        className="w-full text-sm px-2 py-1.5 border-2 rounded-lg border-[#E5DACE] bg-white text-[#1B4F8A]"
        data-testid={`field-${field}`}>
        <option value="">-- Select Class --</option>
        {parentData.classes.map(c => <option key={c.key} value={c.key}>{c.label} ({c.key})</option>)}
      </select>
    );
  }
  if (field === 'chapter_key' && parentData.chapters?.length > 0) {
    const filteredChapters = parentData.selectedClass
      ? parentData.chapters.filter(ch => ch.class_key === parentData.selectedClass)
      : parentData.chapters;
    return (
      <select value={value || ''} onChange={e => onChange(e.target.value)}
        className="w-full text-sm px-2 py-1.5 border-2 rounded-lg border-[#E5DACE] bg-white text-[#1B4F8A]"
        data-testid={`field-${field}`}>
        <option value="">-- Select Chapter --</option>
        {filteredChapters.map(ch => <option key={ch.key} value={ch.key}>{ch.label} (Class {ch.class_key})</option>)}
      </select>
    );
  }
  if (field === 'bloom_level') {
    return (
      <select value={value || ''} onChange={e => onChange(e.target.value)}
        className="w-full text-sm px-2 py-1.5 border-2 rounded-lg border-[#E5DACE] bg-white text-[#1B4F8A]"
        data-testid={`field-${field}`}>
        <option value="">-- Select Bloom Level --</option>
        {BLOOM_LEVELS.map(l => <option key={l} value={l}>{l}</option>)}
      </select>
    );
  }
  if (field === 'color') {
    return (
      <div className="flex gap-1 items-center">
        <Input value={value || ''} onChange={e => onChange(e.target.value)}
          className="text-sm rounded-lg border-2 border-[#E5DACE] flex-1" placeholder={FIELD_PLACEHOLDERS[field]}
          data-testid={`field-${field}`} />
        {value && <span className="w-6 h-6 rounded border shrink-0" style={{ background: value }} />}
      </div>
    );
  }
  return (
    <Input value={value || ''} onChange={e => onChange(e.target.value)}
      className="text-sm rounded-lg border-2 border-[#E5DACE]"
      placeholder={FIELD_PLACEHOLDERS[field] || `Enter ${FIELD_LABELS[field] || field}`}
      data-testid={`field-${field}`} />
  );
}

export default function AdminConfigCentre() {
  const { API, getHeaders } = useAuth();
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [newItem, setNewItem] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [parentData, setParentData] = useState({ classes: [], chapters: [], selectedClass: '' });
  const [filterClass, setFilterClass] = useState('');
  const [filterChapter, setFilterChapter] = useState('');

  const fetchItems = useCallback(async (collection) => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/admin/config/${collection}`, { headers: getHeaders() });
      const data = await res.json();
      setItems(data.items || []);
    } catch {
      toast.error('Failed to load config');
    } finally {
      setLoading(false);
    }
  }, [API, getHeaders]);

  const fetchParentData = useCallback(async () => {
    try {
      const [classRes, chapterRes] = await Promise.all([
        fetch(`${API}/admin/config/config_classes`, { headers: getHeaders() }),
        fetch(`${API}/admin/config/config_chapters`, { headers: getHeaders() }),
      ]);
      const classData = await classRes.json();
      const chapterData = await chapterRes.json();
      setParentData(prev => ({
        ...prev,
        classes: classData.items || [],
        chapters: chapterData.items || [],
      }));
    } catch {}
  }, [API, getHeaders]);

  useEffect(() => {
    fetchParentData();
  }, [fetchParentData]);

  useEffect(() => {
    if (activeSection) {
      fetchItems(activeSection.key);
      setEditingItem(null);
      setNewItem(null);
      setFilterClass('');
      setFilterChapter('');
    }
  }, [activeSection, fetchItems]);

  const handleSave = async (item, isNew) => {
    if (!item.key?.trim() && !item.label?.trim()) {
      toast.error('Key and Label are required');
      return;
    }
    try {
      const collection = activeSection.key;
      if (isNew) {
        const res = await fetch(`${API}/admin/config/${collection}`, {
          method: 'POST', headers: getHeaders(), body: JSON.stringify(item),
        });
        if (!res.ok) throw new Error('Failed');
        toast.success('Item added successfully');
      } else {
        const res = await fetch(`${API}/admin/config/${collection}/${item.key || item.id}`, {
          method: 'PUT', headers: getHeaders(), body: JSON.stringify(item),
        });
        if (!res.ok) throw new Error('Failed');
        toast.success('Item updated');
      }
      fetchItems(collection);
      if (collection === 'config_classes' || collection === 'config_chapters') fetchParentData();
      setEditingItem(null);
      setNewItem(null);
    } catch {
      toast.error('Save failed');
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Delete "${item.label || item.key}"?`)) return;
    try {
      const res = await fetch(`${API}/admin/config/${activeSection.key}/${item.key || item.id}`, {
        method: 'DELETE', headers: getHeaders(),
      });
      if (!res.ok) throw new Error('Failed');
      toast.success('Deleted');
      fetchItems(activeSection.key);
    } catch {
      toast.error('Delete failed');
    }
  };

  const handleToggle = async (item) => {
    try {
      await fetch(`${API}/admin/config/${activeSection.key}/${item.key || item.id}`, {
        method: 'PUT', headers: getHeaders(), body: JSON.stringify({ is_active: !item.is_active }),
      });
      fetchItems(activeSection.key);
    } catch {
      toast.error('Toggle failed');
    }
  };

  const getParentLabel = (field, value) => {
    if (field === 'class_key') {
      const cls = parentData.classes.find(c => c.key === value);
      return cls ? cls.label : value;
    }
    if (field === 'chapter_key') {
      const ch = parentData.chapters.find(c => c.key === value);
      return ch ? ch.label : value;
    }
    return value;
  };

  const section = activeSection;
  const fields = section?.fields || [];

  // Apply filters
  const hasClassFilter = section?.key === 'config_chapters' || section?.key === 'config_microconcepts';
  const hasChapterFilter = section?.key === 'config_microconcepts';
  const filteredItems = items.filter(item => {
    if (filterClass && item.class_key && item.class_key !== filterClass) return false;
    if (filterChapter && item.chapter_key && item.chapter_key !== filterChapter) return false;
    return true;
  });
  const filteredChaptersForFilter = filterClass
    ? parentData.chapters.filter(ch => ch.class_key === filterClass)
    : parentData.chapters;

  const editParentData = {
    ...parentData,
    selectedClass: editingItem?.class_key || newItem?.class_key || '',
  };

  return (
    <div className="min-h-screen" style={{ background: '#FFF8F0' }}>
      {/* Header */}
      <div className="sticky top-0 z-20 bg-white border-b-2 border-[#E5DACE] px-4 py-3 flex items-center gap-3">
        <button onClick={() => navigate('/admin')} data-testid="back-to-admin-btn">
          <ArrowLeft className="w-5 h-5 text-[#1B4F8A]" />
        </button>
        <Settings className="w-5 h-5 text-[#FF6B35]" />
        <h1 className="font-extrabold text-[#1B4F8A] text-lg">Configuration Centre</h1>
        <button className="ml-auto md:hidden" onClick={() => setSidebarOpen(!sidebarOpen)}>
          {sidebarOpen ? <X className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
        </button>
      </div>

      <div className="flex">
        {/* Sidebar */}
        <aside className={`${sidebarOpen ? 'block' : 'hidden'} md:block w-60 min-w-[200px] border-r-2 border-[#E5DACE] bg-white min-h-[calc(100vh-56px)] p-3`}>
          <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3 px-2">Sections</p>
          {CONFIG_SECTIONS.map((s) => {
            const Icon = s.icon;
            const isActive = activeSection?.key === s.key;
            const isChild = s.parent != null;
            return (
              <button key={s.key} data-testid={`config-section-${s.key}`}
                onClick={() => { setActiveSection(s); setSidebarOpen(false); }}
                className={`w-full flex items-center gap-2 px-3 py-2 rounded-xl mb-0.5 text-left text-sm font-semibold transition-all
                  ${isChild ? 'ml-3 text-xs' : ''}
                  ${isActive ? 'bg-[#1B4F8A] text-white' : 'text-slate-600 hover:bg-[#F0E6D8]'}`}>
                <Icon className="w-4 h-4 shrink-0" />
                {s.label}
                {isChild && <span className="text-[10px] opacity-60 ml-auto">{s.parentLabel}</span>}
              </button>
            );
          })}
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-4 md:p-6 overflow-x-auto">
          {!section ? (
            <div className="text-center py-20">
              <Settings className="w-16 h-16 text-[#E5DACE] mx-auto mb-4" />
              <h2 className="text-xl font-bold text-[#1B4F8A] mb-2">Select a configuration section</h2>
              <p className="text-slate-500 text-sm">Choose from the sidebar to manage your app settings</p>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-xl font-bold text-[#1B4F8A]">{section.label}</h2>
                  {section.parent && (
                    <p className="text-xs text-[#8B7355] mt-0.5">
                      Child of: <span className="font-bold">{CONFIG_SECTIONS.find(s => s.key === section.parent)?.label}</span>
                      {' '} (linked via {section.parentLabel} field)
                    </p>
                  )}
                </div>
                <Button data-testid="add-config-item-btn"
                  onClick={() => { setNewItem(Object.fromEntries(fields.map(f => [f, '']))); setEditingItem(null); }}
                  className="bg-[#FF6B35] hover:bg-[#E85D2A] text-white font-bold rounded-full px-4 py-2 text-sm">
                  <Plus className="w-4 h-4 mr-1" /> Add New
                </Button>
              </div>

              {/* New Item Form */}
              {newItem && (
                <div className="mb-4 border-2 border-[#FF6B35] rounded-2xl bg-white p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Plus className="w-4 h-4 text-[#FF6B35]" />
                    <span className="text-sm font-bold text-[#FF6B35]">Add New {section.label.replace(/s$/, '')}</span>
                    <HelpCircle className="w-3.5 h-3.5 text-slate-400 ml-1" title="Fill in fields below. Key must be unique." />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {fields.map(f => (
                      <div key={f}>
                        <label className="text-xs font-bold text-slate-500 block mb-1">
                          {FIELD_LABELS[f] || f.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                          {(f === 'key' || f === 'label') && <span className="text-red-400 ml-0.5">*</span>}
                        </label>
                        <FieldInput field={f} value={newItem[f]}
                          onChange={val => {
                            const updated = { ...newItem, [f]: val };
                            if (f === 'class_key') setParentData(p => ({ ...p, selectedClass: val }));
                            setNewItem(updated);
                          }}
                          parentData={{ ...editParentData, selectedClass: newItem.class_key || '' }}
                          section={section} />
                      </div>
                    ))}
                  </div>
                  <div className="flex gap-2 mt-3 pt-3 border-t border-[#E5DACE]">
                    <Button data-testid="save-new-config-btn" onClick={() => handleSave(newItem, true)}
                      className="bg-[#06D6A0] hover:bg-[#04A77C] text-white font-bold rounded-full text-xs px-4">
                      <Save className="w-3 h-3 mr-1" /> Save
                    </Button>
                    <Button onClick={() => setNewItem(null)} variant="ghost" className="font-bold rounded-full text-xs">
                      <X className="w-3 h-3 mr-1" /> Cancel
                    </Button>
                  </div>
                </div>
              )}

              {/* Filters for chapters and microconcepts */}
              {(hasClassFilter || hasChapterFilter) && (
                <div className="flex gap-2 mb-4 flex-wrap" data-testid="config-filters">
                  {hasClassFilter && (
                    <select value={filterClass} onChange={e => { setFilterClass(e.target.value); setFilterChapter(''); }}
                      className="text-sm px-3 py-1.5 border-2 rounded-lg border-[#E5DACE] bg-white text-[#1B4F8A] font-semibold"
                      data-testid="filter-class">
                      <option value="">All Classes</option>
                      {parentData.classes.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
                    </select>
                  )}
                  {hasChapterFilter && (
                    <select value={filterChapter} onChange={e => setFilterChapter(e.target.value)}
                      className="text-sm px-3 py-1.5 border-2 rounded-lg border-[#E5DACE] bg-white text-[#1B4F8A] font-semibold"
                      data-testid="filter-chapter">
                      <option value="">All Chapters</option>
                      {filteredChaptersForFilter.map(ch => <option key={ch.key} value={ch.key}>{ch.label}</option>)}
                    </select>
                  )}
                  {(filterClass || filterChapter) && (
                    <button onClick={() => { setFilterClass(''); setFilterChapter(''); }}
                      className="text-xs font-bold text-[#E63946] px-2 hover:underline" data-testid="clear-filters">
                      Clear filters
                    </button>
                  )}
                  <span className="text-xs text-slate-400 self-center ml-auto">{filteredItems.length} of {items.length} items</span>
                </div>
              )}

              {/* Table View */}
              {loading ? (
                <div className="space-y-2">
                  {[1,2,3].map(i => <div key={i} className="h-12 bg-[#F0E6D8] rounded-xl animate-pulse" />)}
                </div>
              ) : filteredItems.length === 0 ? (
                <div className="text-center py-10 bg-white rounded-2xl border-2 border-[#E5DACE]">
                  <p className="text-slate-400 text-sm">{items.length === 0 ? 'No items yet. Click "Add New" to create one.' : 'No items match the selected filters.'}</p>
                </div>
              ) : (
                <div className="bg-white border-2 border-[#E5DACE] rounded-2xl overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="config-table">
                      <thead>
                        <tr className="bg-[#F8F4EF] border-b-2 border-[#E5DACE]">
                          <th className="px-3 py-2.5 text-left text-xs font-bold text-[#8B7355] uppercase tracking-wider w-10">Active</th>
                          {fields.map(f => (
                            <th key={f} className="px-3 py-2.5 text-left text-xs font-bold text-[#8B7355] uppercase tracking-wider whitespace-nowrap">
                              {FIELD_LABELS[f] || f.replace(/_/g, ' ')}
                            </th>
                          ))}
                          <th className="px-3 py-2.5 text-right text-xs font-bold text-[#8B7355] uppercase tracking-wider w-24">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredItems.map((item, idx) => (
                          <tr key={item.key || item.id || idx}
                            className={`border-b border-[#F0E6D8] hover:bg-[#FFFBF5] transition-colors ${!item.is_active ? 'opacity-50' : ''}`}
                            data-testid={`config-row-${item.key || idx}`}>
                            {editingItem && (editingItem.key === item.key || editingItem.id === item.id) ? (
                              <>
                                <td className="px-3 py-2">
                                  <button onClick={() => handleToggle(item)}>
                                    {item.is_active ? <ToggleRight className="w-5 h-5 text-[#06D6A0]" /> : <ToggleLeft className="w-5 h-5 text-slate-300" />}
                                  </button>
                                </td>
                                {fields.map(f => (
                                  <td key={f} className="px-2 py-1.5">
                                    <FieldInput field={f} value={editingItem[f]}
                                      onChange={val => {
                                        const updated = { ...editingItem, [f]: val };
                                        setEditingItem(updated);
                                      }}
                                      parentData={{ ...editParentData, selectedClass: editingItem.class_key || '' }}
                                      section={section} />
                                  </td>
                                ))}
                                <td className="px-3 py-2 text-right">
                                  <div className="flex gap-1 justify-end">
                                    <button onClick={() => handleSave(editingItem, false)}
                                      className="p-1.5 bg-[#06D6A0] rounded-lg" data-testid={`save-edit-${item.key}`}>
                                      <Save className="w-3.5 h-3.5 text-white" />
                                    </button>
                                    <button onClick={() => setEditingItem(null)} className="p-1.5 bg-slate-200 rounded-lg">
                                      <X className="w-3.5 h-3.5 text-slate-600" />
                                    </button>
                                  </div>
                                </td>
                              </>
                            ) : (
                              <>
                                <td className="px-3 py-2">
                                  <button onClick={() => handleToggle(item)} data-testid={`toggle-${item.key}`}>
                                    {item.is_active ? <ToggleRight className="w-5 h-5 text-[#06D6A0]" /> : <ToggleLeft className="w-5 h-5 text-slate-300" />}
                                  </button>
                                </td>
                                {fields.map(f => (
                                  <td key={f} className="px-3 py-2 text-[#1B4F8A] whitespace-nowrap max-w-[200px] truncate"
                                    title={String(item[f] || '')}>
                                    {(f === 'class_key' || f === 'chapter_key') ? (
                                      <span className="inline-flex items-center gap-1">
                                        <span className="px-1.5 py-0.5 bg-[#EDE9FE] text-[#8338EC] rounded text-xs font-bold">
                                          {getParentLabel(f, item[f])}
                                        </span>
                                      </span>
                                    ) : f === 'color' && item[f] ? (
                                      <span className="inline-flex items-center gap-1">
                                        <span className="w-4 h-4 rounded border inline-block" style={{ background: item[f] }} />
                                        <span className="text-xs">{item[f]}</span>
                                      </span>
                                    ) : f === 'bloom_level' ? (
                                      <span className="px-1.5 py-0.5 bg-[#FFF3E6] text-[#FF6B35] rounded text-xs font-bold">
                                        {item[f] || '-'}
                                      </span>
                                    ) : (
                                      <span className="text-xs">{item[f] !== undefined && item[f] !== '' ? String(item[f]) : '-'}</span>
                                    )}
                                  </td>
                                ))}
                                <td className="px-3 py-2 text-right">
                                  <div className="flex gap-1 justify-end">
                                    <button onClick={() => setEditingItem({ ...item })} className="p-1.5 hover:bg-[#F0E6D8] rounded-lg"
                                      data-testid={`edit-${item.key}`}>
                                      <Pencil className="w-3.5 h-3.5 text-slate-400" />
                                    </button>
                                    <button onClick={() => handleDelete(item)} className="p-1.5 hover:bg-red-50 rounded-lg"
                                      data-testid={`delete-${item.key}`}>
                                      <Trash2 className="w-3.5 h-3.5 text-red-400" />
                                    </button>
                                  </div>
                                </td>
                              </>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="px-3 py-2 bg-[#F8F4EF] border-t border-[#E5DACE] text-xs text-[#8B7355]">
                    Showing {filteredItems.length}{filteredItems.length !== items.length ? ` of ${items.length}` : ''} item{items.length !== 1 ? 's' : ''}
                    {section.parent && <span> | Parent: {CONFIG_SECTIONS.find(s => s.key === section.parent)?.label}</span>}
                  </div>
                </div>
              )}
            </>
          )}
        </main>
      </div>
    </div>
  );
}
